<template>
  <div class="text-contents flex flex-col gap-4 md:gap-6">
    <div class="text-contents__desktop-infos hidden md:flex">
      <div class="flex items-start justify-between w-full">
        <PageComponentsCarDetailID />
        <PageComponentsCarDetailAutoUncleIframe />
      </div>
    </div>

    <PageComponentsCarDetailFeaturesList class="hidden md:flex" />

    <ul class="text-contents__tabs flex flex-wrap gap-x-6 gap-y-4 md:gap-x-8">
      <li class="text-contents__tabs__item" v-for="(tab, i) in tabs" :key="i">
        <button
          class="text-contents__tabs__item__button px-0 py-2 text-medium-semibold border-b border-b-[#EB5E0E00] hover:border-b-[#EB5E0E80]"
          :title="tab.label"
          :class="{ 'text-contents__tabs__item__button--active !border-b-[#EB5E0Eff]': activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </li>
      <li v-if="locationUrl" class="text-contents__tabs__item__button px-0 py-2 text-medium-semibold border-b border-b-[#EB5E0E00] hover:border-b-[#EB5E0E80]">
        <a @click="trackLocationClick(carDetail?.location || '')" :href="locationUrl" target="_blank" rel="noopener noreferrer" class="flex items-center gap-1">
          <span>{{ t('components.carDetail.textContents.location.label') }}</span>
          <svg
            class="inline-block size-4 text-accent"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      </li>
    </ul>

    <div class="text-contents__content inner-html-text text-regular-normal" v-html="activeTabContent"></div>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLCarDetail } from '~/types/graphql';

  interface EventData {
    event: 'car_detail_location_click';
    location_title: string;
    page_location: string;
    vehicle_id: string;
    make: string;
    model: string;
    manufactured_at: string;
    price: number;
    fuel_type: string;
  }

  const { t, locale } = useI18n();

  const carDetail = inject<GraphQLCarDetail>('carDetail');

  // create the location url based on the location
  let locationUrl: string | undefined;
  if (carDetail?.location) {
    if (carDetail.location.toLowerCase().trim() === '8200 gleisdorf') {
      locationUrl = 'https://maps.app.goo.gl/kz2EjxxpsCntiPUE8';
    }
    if (carDetail.location.toLowerCase().trim() === '7400 oberwart') {
      locationUrl = 'https://maps.app.goo.gl/o2UYdu3eDBZEZSKQ6';
    }
    if (carDetail.location.toLowerCase().trim() === '8200 gleisdorf-süd') {
      locationUrl = 'https://maps.app.goo.gl/o2UYdu3eDBZEZSKQ';
    }
    if (carDetail.location.toLowerCase().trim() === '8200 hofstätten') {
      locationUrl = 'https://maps.app.goo.gl/RwuTh3SZLDQsgN4W8';
    }
  }
  const { push } = useDataLayer();

  const trackLocationClick = (location: string) => {
    const eventData: EventData = {
      event: 'car_detail_location_click',
      location_title: location,
      page_location: window.location.href,
      vehicle_id: carDetail?.internal_vehicle_no || '',
      make: carDetail?.make?.name || '',
      model: carDetail?.model || '',
      manufactured_at: carDetail?.year_of_manufacture?.toString() || '',
      price: carDetail?.asking_price || 0,
      fuel_type: carDetail?.fuel_type || '',
    };
    push(eventData);
  };

  const activeTab = ref('vehicle-data');

  const { tabs } = useVehicleTextContent(carDetail);

  const activeTabContent = computed(() => tabs.find(tab => tab.value === activeTab.value)?.content);
</script>

<style lang="scss" scoped></style>
